import type { JSX } from "react";
import { Button } from "../../../components";
import type { ReviewStepEvent } from "../../../data/chatQuery";

type Step = ReviewStepEvent["steps"][number];

const RISK_LABEL = { high: "High risk", medium: "Medium risk", low: "Low risk" } as const;
const OUTCOME = {
  applied: { label: "Applied", text: "Change applied" },
  kept: { label: "Kept", text: "Kept as drafted — logged as a deviation" },
  skipped: { label: "Later", text: "Left to decide later — still on the agenda" },
} as const;

// [review-compact] A clause that has been decided, left in the thread as its own
// (collapsed) agent message so the review reads as a conversation: each clause
// card stays where it was, the next one stacks below. Built from the progress
// entry alone (short name, why line, decision), so it also reads after a reload.
// Reopening hands the clause back as the current one (onGoto), exactly as the
// classic receipt row does.
export function DecidedReviewCard({
  step,
  pending,
  onOpen,
}: {
  step: Step;
  pending: boolean;
  onOpen: () => void;
}): JSX.Element {
  const state =
    step.decision === "applied" ? "applied" : step.decision === "kept" ? "kept" : "skipped";
  const out = OUTCOME[state];
  const name = step.shortName ?? step.name;
  return (
    <div className="sa-msg sa-msg--assistant">
      <div className={`sa-dc is-${state}`} data-testid="review-decided-card">
        <div className="sa-dc__head">
          {step.displayNumber != null && step.displayNumber.length > 0 && (
            <span className="sa-dc__no">§ {step.displayNumber}</span>
          )}
          <span className="sa-dc__name">{name}</span>
          <span className={`sa-rev__risk is-${step.risk}`}>{RISK_LABEL[step.risk]}</span>
          <span className={`sa-rvi__receipt-pill is-${state}`}>{out.label}</span>
        </div>
        {step.whyLine != null && step.whyLine.length > 0 && (
          <div className="sa-dc__why">{step.whyLine}</div>
        )}
        <div className="sa-dc__foot">
          <span className="sa-dc__out">{out.text}</span>
          <Button
            variant="bare"
            className="sa-dc__reopen"
            disabled={pending}
            onClick={onOpen}
            title="Reopen this clause"
          >
            Reopen
          </Button>
        </div>
      </div>
    </div>
  );
}
